import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useQuery, useQueryClient } from "react-query"

import { getSelectedContactInvoices } from "src/api/Billing/Payments"

const byId = (rows) => rows.map((row) => [row.id, row])

const selectedContactInvoicesKey = ({ marinaSlug, contactId }) => [
  "selectedContactInvoices",
  marinaSlug,
  contactId,
]

const NO_ROWS = { current: new Map(), stale: new Map() }

const useSelectedInvoices = ({
  marinaSlug,
  contactId,
  selection,
  loadedRows,
  enabled,
}) => {
  const queryClient = useQueryClient()
  // `current` rows are trusted until the next refresh; `stale` rows only stand
  // in for their refetch, so the count and total don't drop while it loads.
  const [rows, setRows] = useState(NO_ROWS)

  const keep = useCallback(
    (freshRows) =>
      setRows(({ current, stale }) => ({
        current: new Map([...current, ...byId(freshRows)]),
        stale,
      })),
    []
  )

  useEffect(() => {
    if (enabled && loadedRows.length > 0) keep(loadedRows)
  }, [enabled, loadedRows, keep])

  const pageRows = useMemo(() => new Map(byId(loadedRows)), [loadedRows])

  const missingIds = useMemo(
    () =>
      enabled
        ? selection
            .filter((id) => !pageRows.has(id) && !rows.current.has(id))
            .sort()
        : [],
    [enabled, selection, pageRows, rows]
  )

  const { isError, refetch } = useQuery(
    [...selectedContactInvoicesKey({ marinaSlug, contactId }), missingIds],
    () =>
      getSelectedContactInvoices({ marinaSlug, contactId, ids: missingIds }),
    {
      enabled: missingIds.length > 0,
      refetchOnWindowFocus: false,
      onSuccess: keep,
    }
  )

  // A selection change that leaves the missing ids as they were keeps the
  // query key, so a failed load would stay failed without this retry.
  const lastSelectionRef = useRef(selection)
  useEffect(() => {
    if (selection === lastSelectionRef.current) return
    lastSelectionRef.current = selection
    if (isError) refetch()
  }, [selection, isError, refetch])

  const refresh = useCallback(() => {
    setRows(({ current, stale }) => ({
      current: new Map(),
      stale: new Map([...stale, ...current]),
    }))
    queryClient.invalidateQueries(
      selectedContactInvoicesKey({ marinaSlug, contactId })
    )
  }, [queryClient, marinaSlug, contactId])

  const invoices = useMemo(
    () =>
      enabled
        ? selection
            .map(
              (id) =>
                pageRows.get(id) ?? rows.current.get(id) ?? rows.stale.get(id)
            )
            .filter(Boolean)
        : [],
    [enabled, selection, pageRows, rows]
  )

  return {
    invoices,
    isLoading: missingIds.length > 0 && !isError,
    isError,
    refresh,
  }
}

export default useSelectedInvoices
